/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Symmetry is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Symmetry is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Symmetry, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Symmetry:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Leverages 'code splitting' for some features.
 *  - Is completely custom built (no JS libraries)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Symmetry broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
*   4.  on:cart:after-merge
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - allVariants: an array of all variants
 *  - selectedOptions: an array of currently selected options (e.g. ['Blue', 'Large'])
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:after-merge
 * -------------------------------------------------------------------------------------------------
 * Fires after a list of cart items has finished being dynamically updated after a cart change.
 *
 * How to listen:
 * document.addEventListener('on:cart:after-merge', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Symmetry utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Symmetry has no third-party JavaScript dependencies.
 *
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */


$(document).on('click', '.product__action button', function (e) {
  e.preventDefault();
  var dataId = $(this).parents('.form__body').find('.swatch-element label.current').attr('data-id');
  var obj = {
    id: dataId,
    quantity: 1
  }
  addToCart(obj);
})

function addToCart(obj) {
  $.ajax({
    url: '/cart/add.js',
    type: 'POST',
    data: obj,
    dataType: 'json',
    success: function (data) {
      document.querySelector('cart-form').refresh();
      setTimeout(function () {
        $('.cart-link')[0].click();
      }, 1000)
      getCart();
    },
    error: function (err) {
      console.log('error', err);
    }
  })
}

function getCart() {
  $.ajax({
    url: '/cart.js',
    type: 'GET',
    dataType: 'json',
    success: function (cart) {

      $('.cart-link__count').html(cart.item_count);

    },
    error: function (err) {
      console.log('error', err);
    }
  })
}

/** Faq accordion ***/
window.dynamicFreeShippingMessageUpdate = async () => {
  handleShippingMessage = (
    initialShippingMessage,
    shippingThreshold,
    currencyText
  ) => {
    const cartTotalPrice = parseInt(document.querySelector(".site-header").getAttribute("data-cart-total-price")) / 100;

    const awayShippingMsg = document.querySelectorAll(
      ".free-shipping-away-message"
    );
    const finalShippingMsg = document.querySelectorAll(
      ".free-shipping-success"
    );

    if (cartTotalPrice === 0) {
      Array.from(awayShippingMsg).forEach(item => {
        item.style.display = "none";
      });
      Array.from(finalShippingMsg).forEach(item => {
        item.style.display = "none";
      });
      Array.from(initialShippingMessage).forEach(item => {
        item.style.display = "block";
      });
    } else if (cartTotalPrice > 0) {
      if (cartTotalPrice >= parseInt(shippingThreshold)) {
        Array.from(awayShippingMsg).forEach(item => {
          item.style.display = "none";
        });
        Array.from(initialShippingMessage).forEach(item => {
          item.style.display = "none";
        });
        Array.from(finalShippingMsg).forEach(item => {
          item.style.display = "block";
        });
      } else {
        let awayShipping = (
          parseInt(shippingThreshold) - cartTotalPrice
        ).toFixed(2);
        let awayShippingCurrency = `USD`;
        let cookieCurrency = 'USD';
        if (cookieCurrency === "CAD") {
          awayShipping = Currency.convert(
            awayShipping,
            "USD",
            "CAD"
          ).toFixed(2);
          awayShippingCurrency = "CAD";
        }
        Array.from(finalShippingMsg).forEach(item => {
          item.style.display = "none";
        });
        Array.from(initialShippingMessage).forEach(item => {
          item.style.display = "none";
        });
        Array.from(awayShippingMsg).forEach(item => {
          item.querySelector(
            ".away-money"
          ).innerHTML = `$${awayShipping} ${awayShippingCurrency}`;
          item.style.display = "block";
        });

      }
    }

    if (currencyText) {
      Array.from(initialShippingMessage).forEach(item => {
        const moneyElement = item.querySelector(".money");
        if (moneyElement) {
          moneyElement.innerText = moneyElement.innerText.replace("USD", "");
        }
      });

      Array.from(awayShippingMsg).forEach(item => {
        const awayMoneyElement = item.querySelector(".away-money");
        if (awayMoneyElement) {
          awayMoneyElement.innerText = awayMoneyElement.innerText.replace("USD", " ");
        }
      });
    }
  };
  freeShippingMessageUpdate();
}

if ($('.product-carousel').length) {
  $(".product-carousel").slick({
    dots: false,
    infinite: true,
    slidesToShow: 6,
    slidesToScroll: 1,
    responsive: [
      {
        breakpoint: 1200,
        settings: {
          slidesToShow: 4
        }
      },
      {
        breakpoint: 900,
        settings: {
          slidesToShow: 3
        }
      },
      {
        breakpoint: 600,
        settings: {
          slidesToShow: 2
        }
      }
    ]
  });
}


// swatch code start here
$(function () {
  var body = $("body").attr("data-template");
  if (body == "product") {
    $(".swatch :radio").click(function () {
      var optionIndex = $(this).closest(".swatch").attr("data-option-index");
      var optionValue = $(this).val();
      var selected_availability = $(this).attr("data-available");
      $("#AddToCart").parent().removeClass("disabled");
      $(".flex-wrap li").each(function () {
        var popup_value = $(this).data("value");
        if (popup_value == optionValue) {
          $(".flex-wrap label").removeClass("current");
          $(this).find("label").addClass("current");
        }
      });

      // Update product images as per selected variant
      var $slider_main = $(".slider-content"),
        $slider_thumbnail_li = $(".slider-thumb .gallery__item");
      var selected_size = $(this).attr("data-id");
      $slider_thumbnail_li.each(function () {
        var slide_id = $(this).attr("data-id");
        if (selected_size === slide_id) {
          var slide_active = $(this).attr("data-slick-index");
          $slider_main.slick("slickGoTo", slide_active);
        }
      });

      $(".swatch label").removeClass("current");
      $(this).parent().find("label").addClass("current");

      $(this)
        .closest("form")
        .find(".single-option-selector")
        .eq(optionIndex)
        .val(optionValue)
        .trigger("change");
    });

    // Start -- fix selecting multiple variants per product on product.outfit page
  } else if (body.indexOf("product.outfit") != -1) {
    $(".swatch").each(function () {
      var pid = $(this).attr("data-pid");

      /* start -- Disable add to cart button when no product variant is available */
      let swatchInput = this.querySelectorAll("input");
      let isProductAvailable = [];
      for (i = 0; i < swatchInput.length; i++) {
        let value_avail = swatchInput[i].getAttribute("data-available");
        isProductAvailable.push(value_avail);
      }

      let productAvailablecheck = valueIn => {
        return valueIn == "false";
      };

      if (isProductAvailable.every(productAvailablecheck) === true) {
        $("#AddToCart-" + pid)
          .parent()
          .addClass("disabled");
        $("#AddToCart-" + pid).text("Out of stock");
        $("#AddToCart-" + pid)
          .parent()
          .addClass("disabled__click");
        // $('#AddToCart-'+pid).prop('disabled',true);
      }
      /* end -- Disable add to cart button when no product variant is available */

      $(".swatch-" + pid + " :radio").click(function () {
        var optionIndex = $(this).closest(".swatch").attr("data-option-index");
        var optionValue = $(this).val();
        $("#AddToCart-" + pid).prop("disabled", false);
        $("#AddToCart-" + pid).text("Add to cart");

        $(".button_confirm button").prop("disabled", true);
        $(".button_confirm button").css({ opacity: 0.5 });

        var selected_availability = $(this).attr("data-available");
        $("#AddToCart-" + pid)
          .parent()
          .removeClass("disabled");
        $("#AddToCart-" + pid)
          .parent()
          .removeClass("disabled__click");

        $(this).parent().parent().find("label").removeClass("current");
        $(this).parent().find("label").addClass("current");

        var vid = $(this).attr("data-id");
        $("#productSelect-" + pid + " option").removeAttr("selected");
        $("#productSelect-" + pid + " option[value='" + vid + "']").attr(
          "selected",
          "selected"
        );

        if (selected_availability == "false") {
          $("#AddToCart-" + pid)
            .parent()
            .addClass("disabled");
          $("#AddToCart-" + pid).text("Out of stock");
          $("#AddToCart-" + pid)
            .parent()
            .addClass("disabled__click");
        }
      });
    });
  }
  // End -- fix selecting multiple variants per product on product.outfit page
  // Start -- Fix Gift card button post selector
  else if (body == "product.gift_card") {
    $(".swatch :radio").click(function () {
      var optionIndex = $(this).closest(".swatch").attr("data-option-index");
      var optionValue = $(this).val();
      var selected_availability = $(this).attr("data-available");
      $("#AddToCart").parent().removeClass("disabled");
      $(".flex-wrap div").each(function () {
        var popup_value = $(this).data("value");

        if (popup_value == optionValue) {
          $(".flex-wrap label").removeClass("current");
          $(this).find("label").addClass("current");

          $(this)
            .closest("form")
            .find(".single-option-selector")
            .eq(optionIndex)
            .val(optionValue)
            .trigger("change");
        }
      });
    });
  }
});

document.addEventListener("DOMContentLoaded", () => {
  const productForms = document.querySelectorAll(".product-form");
  productForms.forEach(attachSwatchEventListener);

  function attachSwatchEventListener(formItem) {
    formItem.addEventListener("click", evt => {
      if (
        evt.target.classList.contains("swatch-element") ||
        evt.target.parentElement.classList.contains("swatch-element")
      ) {
        let noVariantSelectElement = evt.currentTarget.querySelector(
          ".js-no-variant-selected"
        );
        if (noVariantSelectElement !== null)
          noVariantSelectElement.classList.remove("js-no-variant-selected");
      }
    });
  }
});

$(document).on('click', '.template-outfit .outfit-submit', function (e) {
  e.preventDefault();
  var id = $(this).parents('#AddToCartForm').find('select[name="id"] option:selected').val();
  var obj = {
    id: id,
    quantity: 1
  }
  addToCart(obj);
});
function addToCart(obj) {
  $.ajax({
    url: '/cart/add.js',
    type: 'POST',
    data: obj,
    dataType: 'json',
    success: function (data) {

      const cartForm = document.querySelector('cart-form');
      if (cartForm && cartForm.enableAjaxUpdate) {
        cartForm.refresh();
      }
      setTimeout(function () {
        $('.cart-link')[0].click();
      }, 700)
      getCartIcon();
      setTimeout(function () {
        window.dynamicFreeShippingMessageUpdate();
      }, 1200)
    },
    error: function (err) {
      console.log('error', err);
    }
  })
}
function getCartIcon() {
  $.ajax({
    url: '/cart.js',
    type: 'GET',
    dataType: 'json',
    success: function (cart) {
      document.querySelector(".site-header").setAttribute("data-cart-total-price", cart.items_subtotal_price)
      $('.cart-link__count').html(cart.item_count);
    },
    error: function (err) {
      console.log('error', err);
    }
  })
}

window.dynamicFreeShippingMessageUpdate = async () => {
  handleShippingMessage = (
    initialShippingMessage,
    shippingThreshold,
    currencyText
  ) => {
    const cartTotalPrice = parseInt(document.querySelector(".site-header").getAttribute("data-cart-total-price")) / 100;

    const awayShippingMsg = document.querySelectorAll(
      ".free-shipping-away-message"
    );
    const finalShippingMsg = document.querySelectorAll(
      ".free-shipping-success"
    );

    if (cartTotalPrice === 0) {
      Array.from(awayShippingMsg).forEach(item => {
        item.style.display = "none";
      });
      Array.from(finalShippingMsg).forEach(item => {
        item.style.display = "none";
      });
      Array.from(initialShippingMessage).forEach(item => {
        item.style.display = "block";
      });
    } else if (cartTotalPrice > 0) {
      if (cartTotalPrice >= parseInt(shippingThreshold)) {
        Array.from(awayShippingMsg).forEach(item => {
          item.style.display = "none";
        });
        Array.from(initialShippingMessage).forEach(item => {
          item.style.display = "none";
        });
        Array.from(finalShippingMsg).forEach(item => {
          item.style.display = "block";
        });
      } else {
        let awayShipping = (
          parseInt(shippingThreshold) - cartTotalPrice
        ).toFixed(2);
        let awayShippingCurrency = `USD`;
        let cookieCurrency = 'USD';
        if (cookieCurrency === "CAD") {
          awayShipping = Currency.convert(
            awayShipping,
            "USD",
            "CAD"
          ).toFixed(2);
          awayShippingCurrency = "CAD";
        }
        Array.from(finalShippingMsg).forEach(item => {
          item.style.display = "none";
        });
        Array.from(initialShippingMessage).forEach(item => {
          item.style.display = "none";
        });
        Array.from(awayShippingMsg).forEach(item => {
          item.querySelector(
            ".away-money"
          ).innerHTML = `$${awayShipping} ${awayShippingCurrency}`;
          item.style.display = "block";
        });
      }
    }

    if (currencyText) {
      Array.from(initialShippingMessage).forEach(item => {
        const moneyElement = item.querySelector(".money");
        if (moneyElement) {
          moneyElement.innerText = moneyElement.innerText.replace("USD", "");
        }
      });

      Array.from(awayShippingMsg).forEach(item => {
        const awayMoneyElement = item.querySelector(".away-money");
        if (awayMoneyElement) {
          awayMoneyElement.innerText = awayMoneyElement.innerText.replace("USD", " ");
        }
      });
    }
  };
  freeShippingMessageUpdate();
}

//  document.addEventListener('DOMContentLoaded', function() {
//   const backButton = document.querySelector('.navigation__tier-3-back');

//   backButton.addEventListener('click', function() {
//       const tier2Parent = document.querySelector('.navigation__tier-2 .navigation__item.navigation__item--with-children').parentNode;
//       tier2Parent.classList.remove('navigation__item--open');
//   });
// });

document.addEventListener("DOMContentLoaded", function () {

  // Navigation 3 tier open close
  var backButtons = document.querySelectorAll(".navigation__tier-3-back");
  backButtons.forEach(function (button) {
    button.addEventListener("click", function (event) {
      var parentItem = event.target.closest(".navigation__tier-2 li.navigation__item--open");
      if (parentItem) {
        parentItem.classList.remove("navigation__item--open");
      }
    });
  });

  function checkWindowSize() {
    var windowWidth = window.innerWidth;
    if (windowWidth < 767) {
      var mainSearch = document.querySelector(".main-search");
      var mobileHeader = document.querySelector(".navigation__mobile-header");
      mobileHeader.appendChild(mainSearch);
      var mobileNavDrawer = document.querySelector(".mobile-navigation-drawer");
      var lastChild = mobileNavDrawer.lastElementChild;
      mobileNavDrawer.removeChild(lastChild);
    }
  }
  checkWindowSize();
});

$(document).ready(function () {
  var $gallery = $(".media-gallery--layout-carousel-beside");
  var $main = $gallery.find(".main__image-slider");
  var $thumbs = $gallery.find(".thumbnails__slider");

  function pauseAllVideos() {
    $gallery.find("video").each(function () {
      try { this.pause(); } catch (e) { }
    });
  }

  function handleSlideMedia() {
    // Pause all videos first
    pauseAllVideos();

    // If the current slide has a video, play it
    var $currentSlide = $main.find(".slick-slide.slick-current");
    var videoEl = $currentSlide.find("video").get(0);
    if (videoEl) {
      if (videoEl.paused || videoEl.ended) {
        // Improve inline/mobile playback reliability
        videoEl.playsInline = true;
        var playPromise = videoEl.play();
        if (playPromise && typeof playPromise.catch === "function") {
          playPromise.catch(function () {
            // Retry muted if autoplay is blocked
            try {
              videoEl.muted = true;
              videoEl.play();
            } catch (e) { }
          });
        }
      }
    }
  }

  function adjustThumbnailHeight() {
    var mainSliderHeight =
      $main.find(".slick-list").outerHeight() ||
      $main.find(".slick-slide.slick-current").outerHeight();
    if (mainSliderHeight) {
      $thumbs.css("height", mainSliderHeight);
    }
  }

  // Bind events before initializing slick
  $main.on("init reInit setPosition", function () {
    adjustThumbnailHeight();
    handleSlideMedia();
  });

  $main.on("beforeChange", function () {
    // Pause immediately when starting a transition to avoid overlap
    pauseAllVideos();
  });

  $main.on("afterChange", function () {
    // After slide change, handle the new slide media
    adjustThumbnailHeight();
    handleSlideMedia();
  });

  // Initialize main slider
  $main.slick({
    dots: false,
    infinite: true,
    slidesToShow: 1,
    slidesToScroll: 1,
    fade: true,
    arrows: false,
    asNavFor: ".thumbnails__slider",
    responsive: [
      {
        breakpoint: 991,
        settings: {
          dots: true,
          arrows: true,
          infinite: false,
          adaptiveHeight: true
        }
      }
    ]
  });

  // Initialize thumbnails slider
  $thumbs.slick({
    dots: false,
    infinite: false,
    slidesToShow: 6.5,
    slidesToScroll: 1,
    vertical: true,
    verticalSwiping: true,
    asNavFor: ".main__image-slider",
    focusOnSelect: true,
    responsive: [
      {
        breakpoint: 1399,
        settings: {
          slidesToShow: 4.5,
          slidesToScroll: 1
        }
      }
    ]
  });

  // Adjust thumbnails height on load and resize
  $(window).on("load resize", adjustThumbnailHeight);
});

window.recommendationsLoaded = function () {
  $(".related-collection-product").slick({
    dots: false,
    infinite: false,
    slidesToShow: 4,
    slidesToScroll: 1,
    arrows: true,
    responsive: [
      {
        breakpoint: 899,
        settings: {
          slidesToShow: 3,
          slidesToScroll: 3
        }
      },
      {
        breakpoint: 567,
        settings: {
          slidesToShow: 1.35,
          slidesToScroll: 1
        }
      }
    ]
  });
};


// swatch element js

var body = $("body").attr("data-template");
$(document).on('click', '.swatch :radio', function () {
  var pid = $(this).closest('.swatch').attr("data-pid");
  $(".swatch-element").find("label").removeClass("current");
  var optionIndex = $(this).closest('.swatch').attr('data-option-index');
  var optionValue = $(this).val();
  $('#AddToCart-' + pid).prop('disabled', false);
  $('#AddToCart-' + pid).text("Add to cart");

  $(".button_confirm button").prop("disabled", true);
  $(".button_confirm button").css({ "opacity": 0.5 });

  var selected_availability = $(this).attr('data-available');
  $('#AddToCart-' + pid).parent().removeClass("disabled");
  $('#AddToCart-' + pid).parent().removeClass("disabled__click");

  $(this).parent().parent().find("label").removeClass("current");
  $(this).parent().find("label").addClass("current");

  var vid = $(this).attr("data-id");
  var vvalue = $(this).attr('value');
  $('body').attr('data-swatchvalue', vvalue);

  $("#productSelect-" + pid + " option").removeAttr("selected");
  $("#productSelect-" + pid + " option[value='" + vid + "']").attr("selected", "selected");
  $('.product-block__details-' + pid).find('input[name="id"]').val(vid);

  if (selected_availability == "false") {
    $('#AddToCart-' + pid).parent().addClass("disabled");
    $('#AddToCart-' + pid).text("Out of stock");
    $('#AddToCart-' + pid).parent().addClass("disabled__click");
  }

  var preorder_state = $(this).attr('data-preorder-state');
  if (selected_availability == "true" && preorder_state == 'ON_PREORDER') {
    $('#AddToCart-' + pid).text("Pre-order");
  }

  // Remove slide up classes from all other product items
  let addToCartButtons = document.querySelectorAll('.cust-product__action button');
  let slideUpDivs = document.querySelectorAll('.product-block__detail');
  let productActionDivs = document.querySelectorAll('.cust-product__action');

  for (let i = 0; i < addToCartButtons.length; i++) {
    addToCartButtons[i].classList.remove('cust-slide-up__add-to-cart');
    slideUpDivs[i].classList.remove('cust-slide-wrap__move');
    productActionDivs[i].classList.remove('custom-visibility');
  }

  // Add slide selector classes to product item
  $('.product-block__details-' + pid).addClass("cust-slide-wrap__move");
  $('#AddToCart-' + pid).addClass("cust-slide-up__add-to-cart");
  $('.cust-product__action-' + pid).addClass("custom-visibility");

  // Fetch price data directly from input
  var formattedPrice = $(this).data('formatted-price');
  var formattedCompareAtPrice = $(this).data('formatted-compare-at-price');
  var discountedPrice = $(this).data('discounted-price');
  var hasSiteDiscount = $(this).data('has-site-discount');

  // DOM blocks for this product
  var $detailsBlock = $('.product-block__details-' + pid);
  var $priceBlock = $detailsBlock.find('.product__price');
  var $wasBlock = $detailsBlock.find('.price__was');
  var $headInner = $detailsBlock.find('.product__head-inner.price');

  // Price UI logic
  if (discountedPrice) { // Discounted: show sale price and strikethrough original price
    $priceBlock.html(discountedPrice);
    $wasBlock.html(formattedPrice).show();
  } else if (formattedCompareAtPrice && formattedCompareAtPrice !== formattedPrice) { // Compare at ONLY when really on sale
    $priceBlock.html(formattedPrice);
    $wasBlock.html(formattedCompareAtPrice).show();
  } else { // Regular price
    $priceBlock.html(formattedPrice);
    $wasBlock.hide();
  }
  if (hasSiteDiscount === true || hasSiteDiscount === "true") {
    $headInner.addClass('has-site-discount');
  } else {
    $headInner.removeClass('has-site-discount');
  }
});


document.addEventListener('click', function (event) {
  var isClickInsideSwatch = false;
  var target = event.target;

  while (target) {
    if (target.classList.contains('swatch-element')) {
      isClickInsideSwatch = true;
      break;
    }
    else if (target.classList.contains('cust-product__action--button')) {
      isClickInsideSwatch = true;
    }
    target = target.parentElement;
  }

  if (!isClickInsideSwatch) {
    var parent = event.target.parentElement;
    while (parent) {
      var productAction = parent.querySelector('.product__action');
      var productDetail = parent.querySelector('.product-block__detail');

      if (productAction) {
        if (productAction.classList.contains('custom-visibility')) {
          if (productAction) productAction.classList.remove('custom-visibility');
          if (productDetail) productDetail.classList.remove('cust-slide-wrap__move');
        }
        break;
      }
      var activeSwatch = parent.querySelectorAll('.swatch-element label');
      activeSwatch.forEach(function (label) {
        label.classList.remove('current');
      });
      parent = parent.parentElement;
    }
  }
});

// swatch element js end

// attentive waitlist trigger js start
(() => {
  /**
   * Decides whether the waitlist trigger should be visible.
   * @param {object|undefined} variant - Selected variant, or undefined when the shopper has made
   *   no selection or only a partial one (e.g. Color chosen but Size not).
   * @param {object} eligibility - Map of variant id to waitlist eligibility.
   * @param {boolean} productHasAvailableVariant - Whether any variant is still purchasable.
   * @returns {boolean} true to show the trigger, false to hide it.
   */
  const shouldShowWaitlistTrigger = (variant, eligibility, productHasAvailableVariant) => {
    if (!variant) return !productHasAvailableVariant;
    return eligibility[variant.id] === true;
  };

  document.addEventListener('on:variant:change', (event) => {
    const section = event.target.closest('.js-product');
    const trigger = section && section.querySelector('#attn-waitlist-trigger');
    if (!trigger) return;

    const eligibilityEl = document.getElementById('attn-waitlist-eligibility');
    if (!eligibilityEl) return;

    let eligibility;
    try {
      eligibility = JSON.parse(eligibilityEl.textContent);
    } catch (e) {
      return;
    }

    trigger.hidden = !shouldShowWaitlistTrigger(
      event.detail.variant,
      eligibility,
      trigger.dataset.productAvailable === 'true'
    );
  });
})();
// attentive waitlist trigger js end